import React, { useState } from "react";
import { X, Search, User } from "lucide-react";
import { useBackToCloseModal } from "../../../../hooks/useBackToCloseModal";
import { normalizeText } from "../../../../utils/search";
import { ClinicScheduleStatusDto } from "../../../../web-api-client";

//MOBILE
interface SpecialtyModalProps {
  specialties: ClinicScheduleStatusDto[];
  setShowBottomSheet: (show: boolean) => void;
  showBottomSheet: boolean;
  handleSpecialtySelect: (item: ClinicScheduleStatusDto) => void;
}

const SpecialtyModal = ({
  specialties,
  setShowBottomSheet,
  showBottomSheet,
  handleSpecialtySelect,
}: SpecialtyModalProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const safeSpecialties = Array.isArray(specialties) ? specialties : [];

  const getSearchScore = (
    clinicName: string,
    description: string,
    searchTerm: string,
  ) => {
    const rawQuery = searchTerm.trim().toLowerCase();
    const normalizedQuery = normalizeText(searchTerm.trim());

    const rawClinicName = (clinicName ?? "").toLowerCase();
    const rawDescription = (description ?? "").toLowerCase();

    const normalizedClinicName = normalizeText(clinicName ?? "");
    const normalizedDescription = normalizeText(description ?? "");

    if (!rawQuery) return 1;

    // Ưu tiên match đúng có dấu trong tên
    if (rawClinicName.includes(rawQuery)) return 100;

    // Ưu tiên match đúng có dấu trong mô tả
    if (rawDescription.includes(rawQuery)) return 60;

    // Fallback: bỏ dấu trong tên
    if (normalizedClinicName.includes(normalizedQuery)) return 30;

    // Fallback: bỏ dấu trong mô tả
    if (normalizedDescription.includes(normalizedQuery)) return 10;

    return 0;
  };

  const filteredItems = safeSpecialties
    .map((sp) => ({
      ...sp,
      _score: getSearchScore(
        sp.clinicName ?? "",
        sp.description ?? "",
        searchTerm,
      ),
    }))
    .filter((sp) => !searchTerm || sp._score > 0)
    .sort((a, b) => b._score - a._score);

  useBackToCloseModal(
    showBottomSheet,
    () => setShowBottomSheet(false),
    "specialty-modal",
  );

  return (
    <div className="pb-[calc(16px+env(safe-area-inset-bottom,0px))]">
      {/* Search and Filter */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Tìm kiếm chuyên khoa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="text-base w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="overflow-y-auto max-h-[62vh]">
        <div className="p-4 space-y-4 pb-16">
          {filteredItems.map((sp) => (
            <div
              key={sp.clinicId}
              className="bg-white border border-gray-200 rounded-xl p-4 hover:border-green-300 hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => {
                handleSpecialtySelect(sp);
                setShowBottomSheet(false);
              }}
            >
              <div className="flex items-start space-x-4">
                {/* Doctor Info */}
                <div className="flex-1 min-w-0 flex flex-col">
                  <span className="text-green-500 font-semibold text-base mb-2 leading-tight">
                    {sp.clinicName}
                  </span>

                  <span className="text-gray-600 text-sm ml-1">
                    {sp.description}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredItems.length === 0 && (
          <div className="p-8 text-center">
            <User className="w-10 h-10 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-base">
              Không tìm thấy chuyên khoa phù hợp
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Thử tìm kiếm với từ khóa khác
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecialtyModal;
